/**
 * Page de liste des contacts avec compteurs et tri.
 * Supporte la vue equipe pour les squad leads.
 */

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getContacts } from "../api/contactApi";
import { getTeamContacts } from "../api/teamApi";
import { useTeamView } from "../contexts/TeamViewContext";
import SortableTable from "../components/SortableTable";
import StatusBadge from "../components/StatusBadge";
import { VISIBILITY_LABELS } from "../utils/constants";
import "./PageStyles.css";

export default function ContactListPage() {
  const [contacts, setContacts] = useState([]);
  const [sortBy, setSortBy] = useState("last_name");
  const [companyFilter, setCompanyFilter] = useState("");
  const [debouncedCompany, setDebouncedCompany] = useState("");
  const navigate = useNavigate();
  const { teamView } = useTeamView();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedCompany(companyFilter), 300);
    return () => clearTimeout(t);
  }, [companyFilter]);

  useEffect(() => {
    loadContacts();
  }, [sortBy, teamView, debouncedCompany]);

  async function loadContacts() {
    const data = teamView
      ? await getTeamContacts(sortBy, debouncedCompany)
      : await getContacts(sortBy, debouncedCompany);
    setContacts(data);
  }

  const columns = [
    { key: "last_name", label: "Nom", sortable: true },
    { key: "first_name", label: "Prénom" },
    { key: "company", label: "Société", sortable: true },
    { key: "job_title", label: "Fonction" },
    { key: "owner_name", label: "Owner", sortable: true },
    { key: "visibility", label: "Visibilité" },
    { key: "todos_ip", label: "To Dos en cours" },
    { key: "actions", label: "Réalisés" },
  ];

  function handleCounterClick(e, contactId) {
    e.stopPropagation();
    navigate(`/contacts/${contactId}`);
  }

  function renderRow(c, _i, hiddenCols) {
    const h = hiddenCols || {};
    // Cliquable si l'utilisateur peut ouvrir/modifier le contact (propriétaire,
    // contact public, ou squad/practice lead du propriétaire) — calculé backend.
    const canClick = Boolean(c.can_edit);
    return (
      <tr
        key={c.id}
        className={canClick ? "clickable-row" : ""}
        onClick={canClick ? () => navigate(`/contacts/${c.id}`) : undefined}
      >
        {!h.last_name && <td><strong>{c.last_name || "—"}</strong></td>}
        {!h.first_name && <td>{c.first_name || "—"}</td>}
        {!h.company && <td>{c.company || "—"}</td>}
        {!h.job_title && <td>{c.job_title || "—"}</td>}
        {!h.owner_name && <td>{c.owner_name || "—"}</td>}
        {!h.visibility && (
          <td>
            <StatusBadge
              value={c.visibility}
              label={VISIBILITY_LABELS[c.visibility]}
            />
          </td>
        )}
        {!h.todos_ip && (
          <td className="counter-cell">
            <span
              className={`counter-link counter-red${canClick ? "" : " counter-disabled"}`}
              onClick={canClick ? (e) => handleCounterClick(e, c.id) : undefined}
            >
              {c.todos_in_progress}
            </span>
          </td>
        )}
        {!h.actions && (
          <td className="counter-cell">
            <span
              className={`counter-link counter-green${canClick ? "" : " counter-disabled"}`}
              onClick={canClick ? (e) => handleCounterClick(e, c.id) : undefined}
            >
              {(c.todos_done || 0) + (c.spontaneous_actions_count || 0)}
            </span>
          </td>
        )}
      </tr>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>{teamView ? "Les contacts de mon équipe" : "Mes Contacts"}</h2>
        {!teamView && (
          <Link to="/contacts/new" className="btn btn-primary">
            Nouveau contact
          </Link>
        )}
      </div>
      <div className="filters-row">
        <input
          type="text"
          className="filter-input"
          placeholder="Rechercher par société…"
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
        />
      </div>
      <SortableTable
        columns={columns}
        rows={contacts}
        renderRow={renderRow}
        sortBy={sortBy}
        onSort={setSortBy}
        emptyMessage="Aucun contact pour le moment."
        storageKey="contacts_table"
      />
    </div>
  );
}
